/**
 * VaultPickerModal.tsx — Fan-aware vault picker for media sends (Plan 05-07, F3 Tier-1 D-05-11/13).
 *
 * Opens from SendComposer for ANY media attach — free media gifts (no price, D-FREE-MEDIA)
 * OR paid PPV. The picker itself is price-agnostic: it returns a durable uploadId; the
 * composer decides free-vs-PPV from whether a price is set.
 * Browses the creator's vault (folders → media) read-only.
 * Passes ?fanId= to surface per-fan sentForPrice/boughtForPrice badges (D-05-13).
 *
 * Key invariants:
 *   - On attach: sets selected media.uploadId as vault_media_id (NEVER the signed URL).
 *   - media.url is SHORT-TTL SIGNED — displayed for previews, never stored.
 *   - uploadId is the durable mediaId used in the PPV chat_product send body.
 *   - Read-only: no upload path (Tier-1 scope).
 *
 * Layout (05-UI-SPEC.md Surface 3):
 *   - Title: "Select media from vault"
 *   - Breadcrumb: "All folders > {folder name}"
 *   - Root: 2-col folder grid (name + thumbnail strip + count chip)
 *   - Media grid inside folder: 3-col w-24 thumbnails
 *   - Selected: ring-2 ring-accent
 *   - "Already purchased" success Badge overlay (boughtForPrice)
 *   - Outline Badge for sentForFree / sentForPrice
 *   - Video: play-icon overlay
 *   - Footer: "Close picker" + "Attach media" (disabled until selected)
 *
 * Pattern: extends Dialog (packages/ui Dialog primitive).
 */

import * as React from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, Button, Badge } from '@crm/ui';
import { nextVaultPage, uniqueVaultItems } from '../lib/vault-pagination.js';
import { vaultApi } from '../lib/api-client.js';
import type { VaultFolderDto, VaultMediaItemDto } from '../lib/api-client.js';
import { MediaLightbox } from './MediaLightbox.js';
import { formatDuration } from '../lib/format.js';

/**
 * The send wire body only accepts picture/video media (send.service.ts shape guard narrows
 * vaultMediaMeta.type to 'picture'|'video'). A vault item of any other type can never be
 * attached, so gate it at the picker (review #4) — the chatter gets a clear affordance
 * instead of going through the whole confirm/translate flow only to hit a post-confirm 400.
 */
const isSupportedMediaType = (type: string): boolean => type === 'picture' || type === 'video';

// ---------------------------------------------------------------------------
// ThumbImage — proxy thumbnail that degrades to a neutral placeholder
// ---------------------------------------------------------------------------

/**
 * Thumbnail <img> that swaps to the caller-supplied placeholder instead of the
 * browser's broken-image icon when the proxy fetch fails (expired signed URL,
 * or the CSAM gate failing closed). Rendered inside the tile buttons, so a
 * failed thumb stays selectable — PPV attach must not be blocked by a missing
 * preview.
 */
export function ThumbImage({ src, fallback }: { src: string; fallback: React.ReactNode }) {
  const [failed, setFailed] = React.useState(false);
  if (failed) return <>{fallback}</>;
  return (
    <img
      src={src}
      alt=""
      className="w-full h-full object-cover"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface VaultPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** The account whose vault to browse */
  accountId: string;
  /** The fan's Maloum member ID — passed as ?fanId= for purchase badges (D-05-13) */
  fanMemberId?: string;
  /**
   * Called when the chatter confirms media selection; passes the durable uploadId of EACH
   * selected item (multi-select, migration 0030). The composer appends these to its set.
   * folderId + hasThumbnail are carried so the composer can render the EU-proxy thumbnail
   * (the send body itself needs only uploadId/type/width/height — folderId is display-only).
   */
  onAttach: (
    media: Array<{
      uploadId: string;
      type: string;
      width: number;
      height: number;
      folderId: string;
      hasThumbnail: boolean;
    }>,
  ) => void;
  /**
   * Max items the chatter may pick in this session (remaining slots = cap − already attached).
   * Selecting is blocked once the picker's own selection reaches this. Defaults to a large number.
   */
  maxSelectable?: number;
}

// ---------------------------------------------------------------------------
// VaultPickerModal
// ---------------------------------------------------------------------------

export function VaultPickerModal({
  open,
  onClose,
  accountId,
  fanMemberId,
  onAttach,
  maxSelectable = 50,
}: VaultPickerModalProps) {
  const [selectedFolder, setSelectedFolder] = React.useState<VaultFolderDto | null>(null);
  // Multi-select (migration 0030): the picker accumulates a set across folders within a session.
  // Each pick carries the folderId it was chosen from so the composer can render its thumbnail.
  const [selectedMedia, setSelectedMedia] = React.useState<Array<VaultMediaItemDto & { folderId: string }>>([]);
  const atSelectionCap = selectedMedia.length >= maxSelectable;
  // Index (within the current folder's media grid) of the item open in the full-screen
  // preview, or null when the preview is closed. Lets a chatter check content before sending.
  const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);

  // Reset state when modal is closed
  React.useEffect(() => {
    if (!open) {
      setSelectedFolder(null);
      setSelectedMedia([]);
      setPreviewIndex(null);
    }
  }, [open]);

  // Leaving a folder (back to the folder grid) closes any open preview.
  React.useEffect(() => {
    setPreviewIndex(null);
  }, [selectedFolder]);

  // Mirror previewIndex into a ref so the close handler can read the LIVE value without
  // capturing it (stale-closure-free) and without a side effect inside a state updater.
  const previewIndexRef = React.useRef<number | null>(previewIndex);
  previewIndexRef.current = previewIndex;

  /**
   * ESC / backdrop close, routed through the picker. While a full-screen preview is
   * open (rendered on top at a higher z-index), close ONLY the preview — the shared
   * Dialog fires onClose on any document ESC, so without this guard one ESC would tear
   * down the whole picker instead of just the preview.
   */
  const handleRequestClose = React.useCallback(() => {
    if (previewIndexRef.current !== null) {
      setPreviewIndex(null); // previewing → collapse the preview only
    } else {
      onClose();
    }
  }, [onClose]);

  const queryClient = useQueryClient();
  // Fetch every requested page; keep cursor 0 and preserve prior pages on retry.
  const foldersQuery = useInfiniteQuery({
    queryKey: ['vault-folders-paged', accountId],
    queryFn: ({ pageParam }) => vaultApi.folders(accountId, undefined, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextVaultPage,
    enabled: open,
    staleTime: 60_000, // 1 min
  });

  // Fetch media inside selected folder (fan-aware when fanMemberId provided)
  const mediaQuery = useInfiniteQuery({
    queryKey: ['vault-media-paged', accountId, selectedFolder?.id, fanMemberId],
    queryFn: ({ pageParam }) =>
      vaultApi.media(accountId, selectedFolder!.id, fanMemberId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextVaultPage,
    enabled: open && selectedFolder !== null,
    staleTime: 30_000,
  });

  const folders = uniqueVaultItems(foldersQuery.data?.pages.flatMap(page => page.folders) ?? [], folder => folder.id);
  const media = uniqueVaultItems(mediaQuery.data?.pages.flatMap(page => page.items) ?? [], item => item.uploadId);

  const handleAttach = () => {
    // Backstop the per-item selection guard: never attach an unsupported media type (review #4).
    const attachable = selectedMedia.filter((m) => isSupportedMediaType(m.type));
    if (attachable.length === 0) return;
    onAttach(
      attachable.map((m) => ({
        uploadId: m.uploadId,
        type: m.type,
        width: m.width,
        height: m.height,
        folderId: m.folderId,
        hasThumbnail: m.hasThumbnail,
      })),
    );
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      title="Select media from vault"
      className="max-w-2xl"
    >
      <div className="flex flex-col" style={{ minHeight: '400px' }}>
        {/* Breadcrumb */}
        <div className="px-4 py-2 border-b border-border text-xs text-text-muted flex items-center gap-1">
          <button
            className="rounded hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
            onClick={() => setSelectedFolder(null)}
          >
            All folders
          </button>
          {selectedFolder && (
            <>
              <span>/</span>
              <span className="text-text truncate max-w-[200px]">{selectedFolder.name}</span>
            </>
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" disabled={foldersQuery.isFetching || mediaQuery.isFetching} onClick={() => {
              setPreviewIndex(null);
              void queryClient.resetQueries({queryKey: selectedFolder
                ? ['vault-media-paged', accountId, selectedFolder.id, fanMemberId]
                : ['vault-folders-paged', accountId], exact:true});
            }}>{selectedFolder ? 'Refresh media' : 'Refresh folders'}</Button>
          </div>
          {/* Folder view */}
          {!selectedFolder && (
            <>
              {foldersQuery.isLoading && (
                <div className="flex items-center justify-center h-32">
                  <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                </div>
              )}
              {foldersQuery.isError && (
                <div className="text-sm text-danger text-center py-8">
                  Could not load this page. Previously loaded items remain available.
                </div>
              )}
              {foldersQuery.data && folders.length === 0 && (
                <div className="text-sm text-text-muted text-center py-8">
                  This creator has no media in their vault.
                </div>
              )}
              {foldersQuery.data && folders.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      className="text-left border border-border rounded-lg p-3 hover:border-accent transition-colors bg-bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
                      onClick={() => setSelectedFolder(folder)}
                    >
                      {/* Thumbnail strip — bytes streamed through the EU proxy (A4) */}
                      <div className="flex gap-1 mb-2">
                        {folder.thumbnails.slice(0, 3).map((thumb, i) => (
                          <div
                            key={`${thumb.uploadId}-${i}`}
                            className="w-12 h-12 rounded overflow-hidden bg-bg-card flex-shrink-0"
                          >
                            {thumb.hasThumbnail && (
                              <ThumbImage
                                src={vaultApi.folderThumbUrl(accountId, folder.id, thumb.uploadId)}
                                fallback={
                                  <div className="w-full h-full flex items-center justify-center bg-bg-hover text-text-subtle text-xs">
                                    <span aria-hidden="true">📎</span>
                                  </div>
                                }
                              />
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Folder info */}
                      <p className="text-sm font-medium text-text truncate">{folder.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-text-muted">
                          {folder.pictureCount}p · {folder.videoCount}v
                        </span>
                        {folder.isManaged && (
                          <span title="System folder created and maintained by Maloum"><Badge variant="info" className="text-xs">Maloum system folder</Badge></span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {foldersQuery.hasNextPage && <Button className="mt-3" variant="outline" disabled={foldersQuery.isFetching} onClick={() => void foldersQuery.fetchNextPage()}>{foldersQuery.isFetchingNextPage ? 'Loading folders…' : foldersQuery.isFetchNextPageError ? 'Retry loading more folders' : 'Load more folders'}</Button>}
            </>
          )}

          {/* Media grid view */}
          {selectedFolder && (
            <>
              {mediaQuery.isLoading && (
                <div className="flex items-center justify-center h-32">
                  <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                </div>
              )}
              {mediaQuery.isError && (
                <div className="text-sm text-danger text-center py-8">
                  Could not load this page. Previously loaded items remain available.
                </div>
              )}
              {mediaQuery.data && media.length === 0 && (
                <div className="text-sm text-text-muted text-center py-8">
                  This creator has no media in their vault.
                </div>
              )}
              {mediaQuery.data && media.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {media.map((item, idx) => {
                    const isSelected = selectedMedia.some((m) => m.uploadId === item.uploadId);
                    const supported = isSupportedMediaType(item.type);
                    // At the selection cap, only already-selected tiles stay interactive (to deselect).
                    const blockedByCap = !isSelected && atSelectionCap;
                    const canPreview = item.hasThumbnail || item.hasFull;
                    return (
                      // Wrapper (not a button): the select control and the preview control
                      // are SIBLINGS — nesting a <button> inside a <button> is invalid HTML.
                      <div
                        key={item.uploadId}
                        className={[
                          'group relative rounded-lg overflow-hidden border-2 transition-all aspect-square',
                          isSelected ? 'border-accent ring-2 ring-accent' : 'border-border hover:border-accent/50',
                          !supported || blockedByCap ? 'opacity-40' : '',
                        ].join(' ')}
                      >
                        {/* Selection order badge — shows which items are in the multi-select set. */}
                        {isSelected && (
                          <span className="absolute top-1 right-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-fg shadow-sm pointer-events-none">
                            ✓
                          </span>
                        )}
                        {/* Select layer — fills the tile; holds the thumbnail. */}
                        <button
                          type="button"
                          disabled={!supported || blockedByCap}
                          className={[
                            'absolute inset-0 h-full w-full',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/60',
                            !supported || blockedByCap ? 'cursor-not-allowed' : '',
                          ].join(' ')}
                          onClick={() => {
                            if (!supported) return; // review #4: unsupported types can't be attached
                            setSelectedMedia((prev) => {
                              if (prev.some((m) => m.uploadId === item.uploadId)) {
                                return prev.filter((m) => m.uploadId !== item.uploadId); // deselect
                              }
                              if (prev.length >= maxSelectable) return prev; // at cap — ignore
                              // Stamp the folder this item was picked from (for the composer thumbnail).
                              return [...prev, { ...item, folderId: selectedFolder.id }];
                            });
                          }}
                          aria-pressed={isSelected}
                          aria-disabled={!supported || blockedByCap}
                          title={
                            !supported
                              ? `Unsupported media type (${item.type}) — cannot be sent`
                              : blockedByCap
                                ? `Selection limit reached (${maxSelectable})`
                                : undefined
                          }
                          aria-label={supported ? `Select ${item.type} media` : `Unsupported media type (${item.type})`}
                        >
                          {/* Thumbnail image — bytes streamed through the EU proxy (A4).
                              h-full: fill the whole aspect-square tile (a leftover fixed
                              h-24 left a bare band above the image that the badges
                              floated over — read as messy). */}
                          <div className="w-full h-full bg-bg-card">
                            {item.hasThumbnail && (
                              <ThumbImage
                                src={vaultApi.mediaThumbUrl(accountId, selectedFolder.id, item.uploadId)}
                                fallback={
                                  <div className="w-full h-full flex items-center justify-center bg-bg-hover text-text-subtle text-xs">
                                    {/* Video kind is signalled by the play-icon overlay below. */}
                                    {item.type !== 'video' && <span aria-hidden="true">📎</span>}
                                  </div>
                                }
                              />
                            )}
                          </div>
                        </button>

                        {/* Video play icon overlay (non-interactive — preview via the expand button) */}
                        {item.type === 'video' && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="bg-scrim rounded-full w-8 h-8 flex items-center justify-center">
                              <svg className="w-4 h-4 text-accent-fg" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M6.3 2.84A1 1 0 005 3.75v12.5a1 1 0 001.3.96l10-4.17a1 1 0 000-1.84l-10-4.17z" />
                              </svg>
                            </div>
                          </div>
                        )}

                        {/* Video duration chip (Maloum vault items carry `length` = seconds).
                            Bottom-LEFT — the expand/preview button owns bottom-right. */}
                        {item.type === 'video' && item.length != null && (
                          <span className="absolute bottom-1 left-1 text-[10px] font-mono text-accent-fg bg-scrim rounded px-1 leading-none py-0.5 pointer-events-none">
                            {formatDuration(item.length)}
                          </span>
                        )}

                        {/* Per-fan send/purchase badges. Maloum returns these fields only
                            when the listing is called with ?fanId= (captured A4 / D-05-13),
                            so they describe history with THE FAN THIS CHAT IS WITH — not
                            global send history. Rendered as SOLID chips (scrim / accent) —
                            the translucent Badge variants washed out over bright thumbnails. */}
                        {item.boughtForPrice !== undefined && (
                          <span className="absolute top-1 left-1 pointer-events-none max-w-[calc(100%-0.5rem)] truncate rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold leading-none text-accent-fg shadow-sm">
                            ✓ Fan purchased{item.boughtForPrice > 0 ? ` €${item.boughtForPrice}` : ''}
                          </span>
                        )}
                        {item.sentForPrice !== undefined && item.boughtForPrice === undefined && (
                          <span className="absolute top-1 left-1 pointer-events-none max-w-[calc(100%-0.5rem)] truncate rounded bg-scrim px-1.5 py-0.5 text-[10px] font-semibold leading-none text-accent-fg shadow-sm">
                            Sent to fan €{item.sentForPrice}
                          </span>
                        )}
                        {item.sentForFree === true && !item.boughtForPrice && !item.sentForPrice && (
                          <span className="absolute top-1 left-1 pointer-events-none max-w-[calc(100%-0.5rem)] truncate rounded bg-scrim px-1.5 py-0.5 text-[10px] font-semibold leading-none text-accent-fg shadow-sm">
                            Sent free to fan
                          </span>
                        )}

                        {/* Expand / preview control — opens the full-screen viewer so the
                            chatter can check the content (and play video) before sending. */}
                        {canPreview && (
                          <button
                            type="button"
                            onClick={() => setPreviewIndex(idx)}
                            aria-label={
                              item.type === 'video'
                                ? 'Preview video full screen'
                                : 'Preview image full screen'
                            }
                            title="Preview full screen"
                            className="absolute bottom-1 right-1 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-scrim text-accent-fg opacity-80 transition-opacity hover:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path d="M3 5a2 2 0 012-2h2a1 1 0 010 2H5v2a1 1 0 01-2 0V5zm12-2h-2a1 1 0 100 2h2v2a1 1 0 102 0V5a2 2 0 00-2-2zM4 12a1 1 0 00-1 1v2a2 2 0 002 2h2a1 1 0 100-2H5v-2a1 1 0 00-1-1zm12 0a1 1 0 00-1 1v2h-2a1 1 0 100 2h2a2 2 0 002-2v-2a1 1 0 00-1-1z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {mediaQuery.hasNextPage && <Button className="mt-3" variant="outline" disabled={mediaQuery.isFetching} onClick={() => void mediaQuery.fetchNextPage()}>{mediaQuery.isFetchingNextPage ? 'Loading media…' : mediaQuery.isFetchNextPageError ? 'Retry loading more media' : 'Load more media'}</Button>}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border">
          <span className="text-xs text-text-muted" aria-live="polite">
            {selectedMedia.length > 0
              ? `${selectedMedia.length} selected${maxSelectable < 50 ? ` (up to ${maxSelectable})` : ''}`
              : 'Tap items to select — you can attach several'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Close picker
            </Button>
            <Button
              size="sm"
              disabled={selectedMedia.length === 0}
              onClick={handleAttach}
            >
              {selectedMedia.length > 1 ? `Attach ${selectedMedia.length} media` : 'Attach media'}
            </Button>
          </div>
        </div>
      </div>

      {/* Full-screen preview (portals to <body> at a higher z-index than the picker).
          Lets a chatter open vault media bigger + play video before attaching. Videos
          stream via HLS, pictures via the /full EU proxy — the browser never touches
          the non-EU CDN. No PPV sale annotation here (vault content, not a sent message). */}
      {selectedFolder &&
        previewIndex !== null &&
        mediaQuery.data &&
        media[previewIndex] && (
          <MediaLightbox
            items={media.map((m) => ({
              uploadId: m.uploadId,
              type: m.type,
              width: m.width,
              height: m.height,
              hasFull: m.hasFull,
            }))}
            startIndex={previewIndex}
            srcFor={(uploadId, variant) =>
              variant === 'hls'
                ? vaultApi.mediaHlsUrl(accountId, selectedFolder.id, uploadId)
                : variant === 'thumb'
                  ? vaultApi.mediaThumbUrl(accountId, selectedFolder.id, uploadId)
                  : vaultApi.mediaFullUrl(accountId, selectedFolder.id, uploadId)
            }
            onClose={() => setPreviewIndex(null)}
          />
        )}
    </Dialog>
  );
}
